import { useState, useCallback, useEffect, useRef } from 'react'
import { Head } from '@inertiajs/react'
import { router } from '@inertiajs/react'
import { HelpCircle } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { SearchBar, Accordion } from '@/components/ui'
import { EditableContent } from '@/components/EditableContent'
import type { EditableContent as EditableContentType } from '@/types'

interface FaqItem {
  id: number
  question: string
  answer: string
  views_count: number
}

interface FAQProps {
  faq_items: FaqItem[]
  query: string | null
  editable_banner: EditableContentType | null
}

export default function FAQ({ faq_items, query, editable_banner }: FAQProps) {
  const [searchQuery, setSearchQuery] = useState(query || '')
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Debounced search
  const debouncedSearch = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      router.get('/faq', value ? { q: value } : {}, {
        preserveState: true,
        preserveScroll: true
      })
    }, 300)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    debouncedSearch(value)
  }

  return (
    <MainLayout
      title="FAQ"
    >
      <Head title="FAQ" />

      {/* Banner éditable (si existe) */}
      {editable_banner && (
        <EditableContent
          content={editable_banner}
          wrapperClassName="mb-8 -mt-8 -mx-4"
        />
      )}

      <div>
        {/* Header */}
        <div className="text-center mb-8">
          <HelpCircle className="w-12 h-12 text-tessan-primary mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            Foire aux questions
          </h1>
          <p className="text-gray-600">
            {faq_items.length} question{faq_items.length > 1 ? 's' : ''} disponible{faq_items.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Search */}
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Rechercher une question..."
          className="mb-8"
        />

        {/* FAQ Items */}
        {faq_items.length > 0 ? (
          <div className="space-y-4">
            {faq_items.map((item) => (
              <Accordion key={item.id} title={item.question}>
                <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap">
                  {item.answer}
                </div>
              </Accordion>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun résultat
            </h3>
            <p className="text-gray-500">
              {searchQuery
                ? `Aucune question trouvée pour "${searchQuery}"`
                : 'Aucune question disponible pour le moment'}
            </p>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
